import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/common/hooks/usePermissions';
import { employeesService } from '@/features/employees/services/employees.service';
import { invoicesService } from '@/features/invoices/services/invoices.service';
import { resolveHistoryBack, validateHistorySearch } from '@/features/invoices/invoice-search';
import { UserInvoicesTable } from '@/features/invoices/components/UserInvoicesTable';
import { useInvoiceActions } from '@/features/invoices/hooks/useInvoiceActions';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_authenticated/admin/invoices/submitted/$userId')({
  validateSearch: validateHistorySearch,
  component: AdminInvoicesUserHistoryPage,
});

function AdminInvoicesUserHistoryPage() {
  const { userId } = Route.useParams();
  const historySearch = Route.useSearch();
  const { page = 1 } = historySearch;
  const navigate = useNavigate({ from: '/admin/invoices/submitted/$userId' });
  const { hasPermission } = usePermissions();
  const { markPaidMutation, download } = useInvoiceActions();

  const { data: employee } = useQuery({
    queryKey: ['invoice-history-employee', userId],
    queryFn: () => employeesService.getEmployee(userId),
    enabled: hasPermission('employees.view'),
    retry: false,
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['user-invoices', userId, page],
    queryFn: () => invoicesService.getUserInvoices(userId, { page, limit: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const displayName = employee?.professionalData?.fullName || employee?.email;

  return (
    <div className="space-y-6">
      <div>
        {/* Voltar restaura os search params (mês, filtro, página, busca) da tela de origem. */}
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1">
          <Link {...resolveHistoryBack(historySearch)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">Histórico de notas</h1>
        <p className="text-muted-foreground">
          {displayName ? `Prestador: ${displayName}` : 'Todas as competências do prestador'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Notas fiscais
          </CardTitle>
          <CardDescription>{total} nota(s) no total</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && !data ? (
            <Skeleton className="h-[160px]" />
          ) : isError && !data ? (
            <div className="text-center py-8 space-y-3 text-muted-foreground">
              <p>Não foi possível carregar as notas.</p>
              <Button variant="outline" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Nenhuma nota fiscal encontrada.{' '}
              <Link {...resolveHistoryBack(historySearch)} className="underline">
                Voltar à lista
              </Link>
            </div>
          ) : (
            <>
              <UserInvoicesTable
                invoices={items}
                onDownload={(id) => void download(id)}
                onMarkPaid={(id) => markPaidMutation.mutate(id)}
                isMarkingPaid={markPaidMutation.isPending}
              />
              <Pagination
                total={total}
                page={page}
                limit={PAGE_SIZE}
                onPageChange={(next) =>
                  navigate({ search: (prev) => ({ ...prev, page: next > 1 ? next : undefined }) })
                }
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
